import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery as useConvexQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { ArrowLeft, Trash2, Shield } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';
import Switch from '../../components/Switch';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAdmin } from '../../contexts/AdminContext';
import { formatDateTime, getPrivilegeLabel } from '../../utils/helpers';
import toast from 'react-hot-toast';

const EDITABLE_PRIVILEGES = [
    'user_management',
    'content_moderation',
    'verification_approval',
    'analytics_view',
    'system_config',
    'basketball_management',
];

export default function AdminDetail() {
    const { adminId } = useParams();
    const navigate = useNavigate();
    const { admin: currentAdmin } = useAdmin();

    const [showRemoveModal, setShowRemoveModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedPrivileges, setSelectedPrivileges] = useState([]);

    const targetAdmin = useConvexQuery(api.platformAdmins.getAdminById, { adminId });
    const adminLogs = useConvexQuery(api.activityLogs.getLogsByAdmin, { adminId, limit: 20 });

    const updatePrivileges = useMutation(api.platformAdmins.updateAdminPrivileges);
    const removeAdmin = useMutation(api.platformAdmins.removeAdmin);

    const isSuperAdmin = targetAdmin?.privileges?.includes('super_admin');

    const handleEditClick = () => {
        setSelectedPrivileges(targetAdmin.privileges.filter(p => p !== 'super_admin'));
        setShowEditModal(true);
    };

    const handleUpdatePrivileges = async () => {
        try {
            await updatePrivileges({
                adminId,
                privileges: selectedPrivileges,
                updatedByAdminId: currentAdmin._id,
            });
            toast.success('Privileges updated successfully');
            setShowEditModal(false);
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleRemove = async () => {
        try {
            await removeAdmin({
                adminId,
                removedByAdminId: currentAdmin._id,
            });
            toast.success('Admin removed successfully');
            navigate('/admins');
        } catch (error) {
            toast.error(error.message);
        }
    };

    const togglePrivilege = (privilege) => {
        setSelectedPrivileges((prev) =>
            prev.includes(privilege)
                ? prev.filter((p) => p !== privilege)
                : [...prev, privilege]
        );
    };

    if (!targetAdmin) {
        return (
            <div className="flex items-center justify-center h-64">
                <LoadingSpinner size="large" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="secondary" onClick={() => navigate('/admins')}>
                        <ArrowLeft size={20} />
                    </Button>
                    <div>
                        <h1 className="text-3xl font-bold">Admin Details</h1>
                        <p className="text-muted-light dark:text-muted-dark">
                            Manage administrator privileges and activity
                        </p>
                    </div>
                </div>
                {!isSuperAdmin && (
                    <div className="flex gap-2">
                        <Button variant="secondary" onClick={handleEditClick}>
                            Edit Privileges
                        </Button>
                        <Button variant="danger" onClick={() => setShowRemoveModal(true)}>
                            <Trash2 size={20} />
                            Remove Admin
                        </Button>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="lg:col-span-1">
                    <div className="text-center">
                        <div className="w-20 h-20 rounded-full bg-accent-light dark:bg-accent-dark flex items-center justify-center mx-auto mb-4">
                            <Shield size={40} className={isSuperAdmin ? 'text-red' : 'text-blue'} />
                        </div>
                        <h2 className="text-xl font-bold mb-2">{targetAdmin.email}</h2>
                        {isSuperAdmin && (
                            <Badge variant="danger" className="mb-4">Super Admin</Badge>
                        )}
                        <p className="text-sm text-muted-light dark:text-muted-dark">
                            Last active: {formatDateTime(targetAdmin.lastActive)}
                        </p>
                    </div>
                </Card>

                <div className="lg:col-span-2 space-y-6">
                    <Card title="Privileges">
                        <div className="flex flex-wrap gap-2">
                            {targetAdmin.privileges.map((privilege) => (
                                <Badge key={privilege} variant="primary">
                                    {getPrivilegeLabel(privilege)}
                                </Badge>
                            ))}
                        </div>
                    </Card>

                    <Card title="Account Information">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-muted-light dark:text-muted-dark mb-1">
                                    Joined
                                </p>
                                <p className="font-medium">{formatDateTime(targetAdmin.createdAt)}</p>
                            </div>
                            <div>
                                <p className="text-sm text-muted-light dark:text-muted-dark mb-1">
                                    Status
                                </p>
                                <Badge variant={targetAdmin.isActive ? 'success' : 'danger'}>
                                    {targetAdmin.isActive ? 'Active' : 'Inactive'}
                                </Badge>
                            </div>
                        </div>
                    </Card>

                    <Card title="Recent Activity">
                        {!adminLogs ? (
                            <LoadingSpinner />
                        ) : adminLogs.length === 0 ? (
                            <p className="text-muted-light dark:text-muted-dark">No activity yet</p>
                        ) : (
                            <div className="space-y-2">
                                {adminLogs.map((log) => (
                                    <div
                                        key={log._id}
                                        className="p-3 rounded-lg bg-accent-light dark:bg-accent-dark"
                                    >
                                        <p className="font-medium">{log.action}</p>
                                        <p className="text-xs text-muted-light dark:text-muted-dark">
                                            {formatDateTime(log.timestamp)}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>
                </div>
            </div>

            {/* Edit Privileges Modal */}
            <Modal
                isOpen={showEditModal}
                onClose={() => setShowEditModal(false)}
                title="Edit Privileges"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="primary" onClick={handleUpdatePrivileges}>
                            Save Changes
                        </Button>
                    </>
                }
            >
                <div className="space-y-3">
                    {EDITABLE_PRIVILEGES.map((privilege) => (
                        <Switch
                            key={privilege}
                            checked={selectedPrivileges.includes(privilege)}
                            onChange={() => togglePrivilege(privilege)}
                            label={getPrivilegeLabel(privilege)}
                        />
                    ))}
                </div>
            </Modal>

            {/* Remove Admin Modal */}
            <Modal
                isOpen={showRemoveModal}
                onClose={() => setShowRemoveModal(false)}
                title="Remove Admin"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setShowRemoveModal(false)}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={handleRemove}>
                            Remove Admin
                        </Button>
                    </>
                }
            >
                <p>
                    Are you sure you want to remove <strong>{targetAdmin.email}</strong> as an admin?
                    This action will revoke all their privileges.
                </p>
            </Modal>
        </div>
    );
}

